import { useEffect, useState } from "react";

type NavbarProps = {
  onApply: () => void;
  isManuallyClosed: boolean;
};

const NAV_ITEMS = [
  { id: "intro", label: "수업 소개" },
  { id: "curriculum", label: "커리큘럼" },
  { id: "info", label: "수업 안내" },
  { id: "faq", label: "FAQ" },
];

export default function Navbar({ onApply, isManuallyClosed }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 40);
    handler();
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);

  const handleScrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    setMobileOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur border-b border-background-200"
          : "bg-transparent"
      }`}
    >
      <div className="w-full px-6 md:px-10 h-[3.75rem] md:h-[4.5rem] flex items-center justify-between">
        <a
          href="#top"
          onClick={(e) => {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          className="flex items-center gap-2 cursor-pointer"
        >
          <span className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-full bg-primary-500 text-background-50 font-heading text-base sm:text-lg">
            日
          </span>
          <span className="font-heading text-base md:text-xl font-bold text-foreground-950 whitespace-nowrap">
            실전 일본어 연구소
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-8">
          {NAV_ITEMS.map((item) => (
            <button
              key={item.id}
              onClick={() => handleScrollTo(item.id)}
              className="text-sm font-medium text-foreground-800 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              {item.label}
            </button>
          ))}
          <button
            onClick={onApply}
            disabled={isManuallyClosed}
            className={`px-5 py-2.5 rounded-md text-sm font-semibold transition-colors whitespace-nowrap ${
              isManuallyClosed
                ? "bg-background-300 text-foreground-400 cursor-not-allowed"
                : "bg-primary-500 hover:bg-primary-600 text-background-50 cursor-pointer"
            }`}
          >
            {isManuallyClosed ? "신청 마감" : "수강 신청하기"}
          </button>
        </nav>

        <button
          className="md:hidden w-9 h-9 flex items-center justify-center text-foreground-900 cursor-pointer"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="메뉴 열기"
        >
          <i className={`ri-${mobileOpen ? "close" : "menu"}-line text-2xl`}></i>
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200 px-6 py-4 flex flex-col gap-3">
          {NAV_ITEMS.map((item) => (
            <button
              key={item.id}
              onClick={() => handleScrollTo(item.id)}
              className="text-left text-sm font-medium text-foreground-800 py-2 cursor-pointer"
            >
              {item.label}
            </button>
          ))}
          <button
            onClick={() => {
              onApply();
              setMobileOpen(false);
            }}
            disabled={isManuallyClosed}
            className={`mt-2 px-5 py-3 rounded-md text-sm font-semibold whitespace-nowrap ${
              isManuallyClosed
                ? "bg-background-300 text-foreground-400 cursor-not-allowed"
                : "bg-primary-500 text-background-50 cursor-pointer"
            }`}
          >
            {isManuallyClosed ? "신청 마감" : "수강 신청하기"}
          </button>
        </div>
      )}
    </header>
  );
}